import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "config-summary" }
const _hoisted_2 = { class: "config-summary__title" }
const _hoisted_3 = {
  key: 0,
  class: "config-summary__content"
}
const _hoisted_4 = { class: "config-summary__products" }
const _hoisted_5 = { class: "config-summary__products-title" }
const _hoisted_6 = { class: "config-summary__products-total" }
const _hoisted_7 = {
  key: 0,
  class: "config-summary__product"
}
const _hoisted_8 = {
  key: 1,
  class: "config-summary__add-product-info"
}
const _hoisted_9 = {
  key: 0,
  class: "config-summary__decorations"
}
const _hoisted_10 = { class: "config-summary__decorations-summary" }
const _hoisted_11 = { class: "config-summary__decorations-title" }
const _hoisted_12 = { class: "config-summary__decorations-total" }
const _hoisted_13 = { class: "config-summary__decoration" }
const _hoisted_14 = {
  key: 1,
  class: "config-summary__addons"
}
const _hoisted_15 = { class: "config-summary__addons-summary" }
const _hoisted_16 = { class: "config-summary__addons-title" }
const _hoisted_17 = { class: "config-summary__addons-total" }
const _hoisted_18 = { class: "config-summary__addons" }
const _hoisted_19 = {
  key: 2,
  class: "config-summary__shipping"
}
const _hoisted_20 = { class: "config-summary__shipping-summary" }
const _hoisted_21 = { class: "config-summary__shipping-title" }
const _hoisted_22 = { class: "config-summary__shipping-total" }
const _hoisted_23 = { class: "config-summary__addons" }
const _hoisted_24 = { class: "config-summary__total" }
const _hoisted_25 = { class: "config-summary__total-title" }
const _hoisted_26 = { class: "config-summary__total-price" }
const _hoisted_27 = {
  key: 1,
  class: "config-summary__fee-info p-short"
}
const _hoisted_28 = {
  key: 2,
  class: "config-summary__empty p-short"
}

import { computed, inject } from 'vue';

import { configuratorStateKey } from '../ProductConfiguratorKeys';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorSummaryRow from './ConfiguratorSummaryRow.vue';
import { getAddonTitle } from '@/helpers/printess';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConfiguratorSummary',
  props: {
  variants: {
    type: Array,
    required: true
  },
  decorations: {
    type: Array,
    required: true
  },
  canAddToCart: {
    type: Boolean,
    required: true
  },
  hasDecoration: {
    type: Boolean,
    required: true
  },
  hasAddons: {
    type: Boolean,
    required: true
  },
  isGiftCard: {
    type: Boolean,
    default: false
  },
  giftCardFreightPrice: {
    type: Number,
    default: 0
  }
},
  setup(__props) {

const { formatPrice, cart, isAuthorized, channel } = useContext();
const { websiteText } = useWebsiteTexts();
const { configuratorState } = inject(configuratorStateKey);

const props = __props;

const emptySummary = computed(
  () => !props.canAddToCart && !props.hasDecoration && !props.hasAddons
);
const displayAddProductText = computed(
  () => !props.canAddToCart && (props.hasDecoration || props.hasAddons)
);
const displayAdditionalFeeText = computed(
  () => selectedDecorations.value?.[0]?.addons?.length
);

const selectedVariants = computed(() => {
  if (!props.canAddToCart) return [];
  const selected = props.variants.filter((v) => {
    return configuratorState.value.quantities[v.systemId];
  });

  let variants = selected.map(s => {
    const isFlexible = s.fields.IsFlexibleType == true;
    const quantity = configuratorState.value.quantities[s.systemId];
    const price = (isFlexible ? configuratorState.value.customPrice : configuratorState.value.activePriceBracket[s.systemId]?.price) ?? 0;
    const originalPrice = getOriginalPrice(s);
    const currentPrice = getCurrentPrice(s);
    const campaignPercentage = getCampaignPercentage(
      currentPrice,
      originalPrice
    );
    const articleNumberForDisplay = s.fields.NAVArticleNumber || s.articleNumber;
    const country =
      configuratorState.value.recipientCountry.name ||
      cart?.value.countries[0].text;
    const giftcardType = configuratorState.value.giftcardType;
    return {
      name:
        s.fields?.Color?.[0]?.name ||
        s.fields?.Color?.[0]?.value ||
        s.fields?.GiftcardType + ' - ' + country + ' - ' + giftcardType,
      id: s.articleNumber,
      quantity,
      price,
      originalPrice,
      currentPrice,
      campaignPercentage,
      articleNumberForDisplay: articleNumberForDisplay
    };
  });
  return variants;
});

const selectedDecorations = computed(() => {
  if (!props.hasDecoration) return [];

  const placement = props.decorations.filter((v) => {
    return v.systemId === configuratorState.value.selections.selectedPlacement;
  });

  return placement.map((p) => {
    const technique = p.techniques.find(
      (t) => t.systemId === configuratorState.value.selections.selectedTechnique
    );
    const techniqueVariant = technique.variants.find(
      (v) =>
        v.systemId ===
        configuratorState.value.selections.selectedTechniqueVariant
    );
    const techniqueCode = technique.code;
    const variantType = techniqueVariant.nrOfColors;
    const name = websiteText(
      `decoration__technique_${techniqueCode.toLowerCase()}`
    ).value;
    let variantName = techniqueVariant.name || '';
    if (variantType && !techniqueVariant.name) {
      variantName =
        variantType === 1
          ? websiteText('decoration__variant_color_single', {
              amount: variantType
            }).value
          : websiteText('decoration__variant_color_multiple', {
              amount: variantType
            }).value;
    }

    const priceBracket =
      configuratorState.value.selections?.selectedTechniqueVariantPrices?.reduce(
        (curr, b) => {
          return b.bracket > curr.bracket &&
            b.bracket <= Math.max(configuratorState.value.sumQuantity, 1)
            ? b
            : curr;
        },
        { bracket: 0, price: 0 }
      );

    const displayName = variantName || name;
    const quantity = configuratorState.value.sumQuantity || 1;
    const price = priceBracket?.price || 0;
    const addons =
      configuratorState.value.selections.selectedTechniqueVariantAddons;

    return {
      name: displayName,
      id: p.systemId,
      quantity,
      price,
      addons
    };
  });
});

const selectedAddons = computed(() => {
  if (!props.hasAddons) return [];

  return configuratorState.value.selections.selectedAddons.map((a) => {
    return {
      name: getAddonTitle(a.variant),
      id: a.systemId,
      quantity: a.variant.ignoreGiftCardQuantity
        ? 1
        : configuratorState.value.sumQuantity,
      price: a.variant.prices[0]?.price || 0
    };
  });
});

const giftcardShipping = computed(() => {
  if (!props.isGiftCard) return [];

  return [
    {
      name: websiteText('giftcard__shipping_name').value,
      id: 'PKFRAKT',
      quantity: configuratorState.value.sumQuantity,
      price: props.giftCardFreightPrice
    }
  ];
});

function getCurrentPrice(row) {
  return row.campaignPrice ?? row.price;
}

function getOriginalPrice(row) {
  return row.originalPrice >= row.price ? row.originalPrice : row.price;
}

function getCampaignPercentage(current, original) {
  if (current < original) {
    return formatPrice(
      Math.abs(((original - current) / original) * -100),
      undefined,
      '',
      '',
      'decimal'
    );
  }

  return 0;
}

const summaryProducts = computed(() => {
  return selectedVariants.value.reduce((acc, curr) => {
    var price = curr.price ?? curr.currentPrice ?? curr.originalPrice;
    if (
      channel.value.combineGiftCardPriceWithShipping &&
      props.isGiftCard &&
      !isAuthorized()
    ) {
      price += props.giftCardFreightPrice;
    }
    return acc + price * curr.quantity;
  }, 0);
});

const summaryDecorations = computed(() => {
  return selectedDecorations.value.reduce((acc, curr) => {
    return acc + curr.price * curr.quantity + summaryFees.value;
  }, 0);
});

const summaryAddons = computed(() => {
  return selectedAddons.value.reduce((acc, curr) => {
    return acc + curr.price * curr.quantity + summaryFees.value;
  }, 0);
});

const summaryShipping = computed(() => {
  if (
    !props.isGiftCard ||
    (channel.value.combineGiftCardPriceWithShipping &&
      props.isGiftCard &&
      !isAuthorized())
  )
    return 0;
  return configuratorState.value.sumQuantity * props.giftCardFreightPrice;
});

const summaryFees = computed(() => {
  return selectedDecorations.value.reduce((acc, curr) => {
    return (
      acc + (curr.addons || 0) &&
      curr.addons.reduce((addonAcc, addon) => addonAcc + addon.price, 0)
    );
  }, 0);
});

const summaryTotal = computed(() => {
  return (
    summaryProducts.value +
    summaryDecorations.value +
    summaryAddons.value +
    summaryShipping.value
  );
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h5", _hoisted_2, _toDisplayString(_unref(websiteText)('checkout__summary').value), 1),
    (!emptySummary.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass([
            'config-summary__products-summary',
            { 'config-summary__products-summary--faded': displayAddProductText.value }
          ])
            }, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(websiteText)('checkout__summary_products').value), 1),
              _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(formatPrice)(summaryProducts.value)), 1)
            ], 2),
            (selectedVariants.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedVariants.value, (variant) => {
                    return (_openBlock(), _createBlock(ConfiguratorSummaryRow, {
                      key: variant.id,
                      "is-decoration": false,
                      item: variant,
                      "gift-card-freight-price": _ctx.$props.giftCardFreightPrice,
                      "is-gift-card": props.isGiftCard
                    }, null, 8, ["item", "gift-card-freight-price", "is-gift-card"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (displayAddProductText.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(websiteText)('decoration__add_product_info').value), 1))
              : _createCommentVNode("", true)
          ]),
          (selectedDecorations.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(websiteText)('decoration__decorations').value), 1),
                  _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(formatPrice)(summaryDecorations.value)), 1)
                ]),
                _createElementVNode("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedDecorations.value, (variant) => {
                    return (_openBlock(), _createBlock(ConfiguratorSummaryRow, {
                      key: variant.id,
                      "is-decoration": true,
                      item: variant
                    }, null, 8, ["item"]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          (selectedAddons.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(websiteText)('giftcard__addons').value), 1),
                  _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(formatPrice)(summaryAddons.value)), 1)
                ]),
                _createElementVNode("div", _hoisted_18, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedAddons.value, (variant) => {
                    return (_openBlock(), _createBlock(ConfiguratorSummaryRow, {
                      key: variant.id,
                      "is-decoration": true,
                      item: variant
                    }, null, 8, ["item"]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          (__props.isGiftCard && selectedVariants.value.length && summaryShipping.value > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("div", _hoisted_21, _toDisplayString(_unref(websiteText)('giftcard__shipping').value), 1),
                  _createElementVNode("div", _hoisted_22, _toDisplayString(_unref(formatPrice)(summaryShipping.value)), 1)
                ]),
                _createElementVNode("div", _hoisted_23, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(giftcardShipping.value, (variant) => {
                    return (_openBlock(), _createBlock(ConfiguratorSummaryRow, {
                      key: variant.id,
                      "is-decoration": true,
                      item: variant
                    }, null, 8, ["item"]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_24, [
            _createElementVNode("div", _hoisted_25, _toDisplayString(_unref(websiteText)('cart__total').value), 1),
            _createElementVNode("div", _hoisted_26, _toDisplayString(_unref(formatPrice)(summaryTotal.value)), 1)
          ])
        ]))
      : _createCommentVNode("", true),
    (displayAdditionalFeeText.value && __props.canAddToCart)
      ? (_openBlock(), _createElementBlock("p", _hoisted_27, _toDisplayString(_unref(websiteText)('decoration__additional_fee', {
          amount: _unref(formatPrice)(summaryFees.value)
        }).value), 1))
      : _createCommentVNode("", true),
    (emptySummary.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(_unref(websiteText)('decoration__empty_summary').value), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})